import { useEffect, useRef, useState } from "react";
import { CheckCircle2, RotateCcw, AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FileDrop, MappingEditor, StatusPill, SummaryGrid } from "@/components/FileImport";
import ConfirmDialog from "@/components/ConfirmDialog";
import { isoToDmy } from "@/components/DatePicker";
import { SlipActions, SlipButton } from "@/components/advance/SlipActions";
import { api, errMsg, newKey } from "@/lib/api";
import { inr } from "@/lib/format";
import { toast } from "sonner";

const FIELDS = [["code", "Employee ID", true], ["name", "Employee Name", true], ["date", "Date", true], ["amount", "Advance Amount", true], ["purpose", "Purpose", true]];

function EditCell({ value, onChange, testid, w }) {
  return <Input value={value} onChange={(e) => onChange(e.target.value)} className={`h-8 px-2 text-xs ${w}`} data-testid={testid} />;
}

function Preview({ raw, rows, include, toggle, edit, remove }) {
  return (
    <div className="bg-white rounded-xl border overflow-auto max-h-[520px]" data-testid="bulk-advance-preview-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
          <tr>{["#", "Employee ID", "Employee Name", "Date", "Amount (₹)", "Purpose", "Status", ""].map((h, i) => <th key={i} className="px-2 py-2 text-left whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>
          {raw.map((x, i) => {
            const r = rows[i] || {};
            const t = (k) => `bulk-advance-${k}-${i}`;
            return (
              <tr key={i} className={`border-t border-slate-100 align-top ${r.status === "invalid" ? "bg-red-50/50" : r.status === "duplicate" ? "bg-amber-50/50" : ""}`} data-testid={`bulk-advance-row-${i}`}>
                <td className="px-2 py-2 text-slate-400">{i + 1}</td>
                <td className="px-2 py-1.5"><EditCell value={x.code} onChange={(v) => edit(i, "code", v)} testid={t("code")} w="w-24" /></td>
                <td className="px-2 py-1.5"><EditCell value={x.name} onChange={(v) => edit(i, "name", v)} testid={t("name")} w="w-40" /></td>
                <td className="px-2 py-1.5"><EditCell value={x.date} onChange={(v) => edit(i, "date", v)} testid={t("date")} w="w-28" />{r.date && <div className="text-[10px] text-slate-400 mt-0.5">{isoToDmy(r.date)}</div>}</td>
                <td className="px-2 py-1.5"><EditCell value={x.amount} onChange={(v) => edit(i, "amount", v)} testid={t("amount")} w="w-24 text-right money" /></td>
                <td className="px-2 py-1.5"><EditCell value={x.purpose} onChange={(v) => edit(i, "purpose", v)} testid={t("purpose")} w="w-28" /></td>
                <td className="px-2 py-2 space-y-1 min-w-[220px]" data-testid={t("status")}>
                  {r.status && <StatusPill status={r.status === "duplicate" ? "possible_duplicate" : r.status} />}
                  {r.status === "invalid" && <div className="text-red-700" data-testid={t("error")}>{r.error}</div>}
                  {r.status === "duplicate" && (
                    <label className="flex items-start gap-2 cursor-pointer text-amber-800">
                      <Checkbox checked={include.has(i)} onCheckedChange={(v) => toggle(i, v)} data-testid={t("include")} />
                      <span>{r.error}. Tick to save anyway.</span>
                    </label>
                  )}
                </td>
                <td className="px-2 py-1.5"><button onClick={() => remove(i)} className="p-1.5 rounded text-slate-400 hover:text-red-700 hover:bg-red-50 transition-colors" title="Remove row" data-testid={t("remove")}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function BulkAdvances({ disabled, onSaved }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [raw, setRaw] = useState([]);
  const [rows, setRows] = useState([]);
  const [include, setInclude] = useState(new Set());
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [summary, setSummary] = useState(null);
  const [batch, setBatch] = useState(newKey());
  const timer = useRef(null);
  const first = useRef(true);

  const parse = async (f, mapping) => {
    if (!/\.(xlsx|xls)$/i.test(f.name)) { toast.error("Please upload an Excel file (.xlsx or .xls)"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/advances/parse", fd);
      first.current = true;
      setFile(f); setParsed(data); setRaw(data.raw); setRows(data.rows); setInclude(new Set()); setSummary(null); setBatch(newKey());
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!raw.length) { setRows([]); return; }
    setChecking(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post("/advances/validate", { rows: raw }).then((r) => setRows(r.data.rows)).catch((e) => toast.error(errMsg(e))).finally(() => setChecking(false));
    }, 450);
    return () => clearTimeout(timer.current);
  }, [raw]);

  const edit = (i, k, v) => setRaw((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const remove = (i) => { setInclude(new Set()); setRaw((rs) => rs.filter((_, j) => j !== i)); };
  const toggle = (i, v) => { const s = new Set(include); v ? s.add(i) : s.delete(i); setInclude(s); };
  const reset = () => { setParsed(null); setSummary(null); setRaw([]); setRows([]); };

  const synced = rows.length === raw.length && !checking;
  const ready = synced ? rows.filter((r) => r.status === "ok" || (r.status === "duplicate" && include.has(r.row))) : [];
  const total = ready.reduce((a, r) => a + Math.round(r.amount * 100), 0) / 100;
  const invalid = rows.filter((r) => r.status === "invalid").length;

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/advances/bulk", { rows: raw, include: [...include], batch_key: batch, filename: parsed.filename });
      setSummary(data); setAsk(false); onSaved?.();
      toast.success(`${data.saved} advances saved (${inr(data.amount)})`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (summary) return (
    <div className="space-y-4 fade-up" data-testid="bulk-advance-summary">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-5 w-5" /> Advances saved from {parsed.filename}</div>
      <SummaryGrid testid="bulk-advance-summary-grid" items={[
        ["total", "Rows", summary.total], ["saved", "Saved", summary.saved, "text-emerald-700"], ["amount", "Total advances", inr(summary.amount), "text-emerald-700 text-lg"],
        ["purchase", "Purchase", inr(summary.by_purpose.Purchase)], ["cash", "Cash Advance", inr(summary.by_purpose["Cash Advance"])],
        ["skipped", "Skipped", summary.duplicates + summary.invalid + summary.already, "text-orange-700"],
      ]} />
      {summary.saved_rows.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="bulk-advance-results">
          <div className="px-5 py-3 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <span className="font-heading font-semibold text-slate-800">{summary.saved_rows.length} advance slip{summary.saved_rows.length > 1 ? "s" : ""} generated</span>
            <SlipActions ids={summary.saved_rows.map((r) => r.id)} prefix="bulk-advance-slips" printLabel="Print All Advance Slips" downloadLabel="Download All Slips (PDF)" />
          </div>
          <div className="overflow-x-auto max-h-[480px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>{["Employee", "Department", "Branch", "Date", "Purpose", "Amount", ""].map((h, i) => <th key={i} className={`px-4 py-2.5 ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {summary.saved_rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`bulk-advance-result-${r.id}`}>
                    <td className="px-4 py-2"><span className="money text-blue-800 font-semibold">{r.code}</span> {r.name}</td>
                    <td className="px-4 py-2">{r.department || "-"}</td>
                    <td className="px-4 py-2">{r.branch || "-"}</td>
                    <td className="px-4 py-2 money">{isoToDmy(r.date)}</td>
                    <td className="px-4 py-2">{r.purpose}</td>
                    <td className="px-4 py-2 text-right money font-semibold">{inr(r.amount)}</td>
                    <td className="px-4 py-2 text-right"><SlipButton ids={[r.id]} label="Print" testid={`bulk-advance-print-${r.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Button variant="outline" onClick={reset} data-testid="bulk-advance-another"><RotateCcw className="h-4 w-4" /> Upload another file</Button>
    </div>
  );

  if (!parsed) return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Columns in this order: <b>A</b> Employee ID · <b>B</b> Employee Name · <b>C</b> Date (DD-MM-YYYY) · <b>D</b> Advance Amount · <b>E</b> Purpose (Purchase or Cash Advance).</p>
      <div className={disabled ? "opacity-50 pointer-events-none" : ""} data-testid="bulk-advance-drop-wrap"><FileDrop onFile={(f) => !disabled && parse(f)} busy={busy} testid="bulk-advance" template="advances" excelOnly /></div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center text-sm text-slate-600"><span>File: <b>{parsed.filename}</b> · {raw.length} rows · edit any cell to correct it</span><Button variant="outline" size="sm" onClick={reset} data-testid="bulk-advance-reset"><RotateCcw className="h-4 w-4" /> New file</Button></div>
      {parsed.missing_fields.length > 0 ? (
        <>
          <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="bulk-advance-missing"><AlertTriangle className="h-4 w-4" /> Could not detect: {parsed.missing_fields.join(", ")}. Map the columns and click Apply.</div>
          <MappingEditor key={JSON.stringify(parsed.mapping)} headers={parsed.headers} mapping={parsed.mapping} fields={FIELDS} busy={busy} testid="bulk-advance" onApply={(m) => parse(file, m)} />
        </>
      ) : (
        <>
          <Preview raw={raw} rows={rows} include={include} toggle={toggle} edit={edit} remove={remove} />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border p-4">
            <div className="text-sm text-slate-600" data-testid="bulk-advance-ready-text">
              {checking ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /> Re-checking…</span>
                : <>{ready.length} {ready.length === 1 ? "advance" : "advances"} ready · total <b className="money">{inr(total)}</b>{invalid > 0 && <span className="text-red-700"> · {invalid} invalid</span>} · {raw.length - ready.length} will be skipped</>}
            </div>
            <Button className="bg-blue-800 hover:bg-blue-900" disabled={disabled || busy || !synced || ready.length === 0} onClick={() => setAsk(true)} data-testid="bulk-advance-commit-btn">Confirm & Save Advances</Button>
          </div>
        </>
      )}
      <ConfirmDialog open={ask} onOpenChange={setAsk} busy={busy} onConfirm={commit} confirmText={`Save ${ready.length} advances`} testid="bulk-advance-confirm-dialog"
        title="Confirm & save advances" description={`Save ${ready.length} advances totalling ${inr(total)} to the employees' advance balances. Invalid rows and unticked duplicates are skipped. No salary deduction is made.`} />
    </div>
  );
}
